import { getApiBase } from './config';
import { getDeviceId } from '../widget-lib/device-id-bridge';
import {
  clearEmbedSession,
  clearGlobalEmbedSession,
  getGlobalEmbedSession
} from '../widget-lib/embed-session-store';
import { getSuperadminOrgId, shouldSendSuperadminOrgHeader } from '../widget-lib/superadmin-org';

export type QueryRecord = Record<string, string | number | boolean | null | undefined>;

export type ApiRequestInit = RequestInit & {
  /** Если задан — добавляется заголовок Authorization. */
  accessToken?: string | null;
  /** Не вызывать обновление access по refresh (избежать рекурсии и для публичных вызовов). */
  skipEmbedTokenRefresh?: boolean;
  /** Не отправлять X-Impersonate-Organization-Id (например, глобальный список студий суперадмина). */
  skipImpersonation?: boolean;
};

/** Элемент `detail` при 422 от FastAPI / Pydantic (массив ошибок валидации). */
export type ApiValidationIssue = {
  type?: string;
  msg: string;
  loc?: (string | number)[];
  input?: unknown;
};

function formatValidationLoc(loc: (string | number)[] | undefined): string {
  if (!loc?.length) return '';
  const tail = loc.filter((x) => x !== 'body' && x !== 'query' && x !== 'path' && x !== 'header');
  if (!tail.length) return '';
  return tail.map(String).join('.');
}

/** Текст для `ApiError.detail`: строка, массив ошибок валидации или объект. */
export function formatApiErrorDetail(detail: unknown): { message: string; items?: ApiValidationIssue[] } {
  if (detail == null) {
    return { message: 'Неизвестная ошибка' };
  }
  if (typeof detail === 'string') {
    return { message: detail };
  }
  if (Array.isArray(detail)) {
    const items: ApiValidationIssue[] = [];
    const lines: string[] = [];
    let i = 0;
    for (const entry of detail) {
      if (entry && typeof entry === 'object' && 'msg' in entry) {
        const e = entry as Record<string, unknown>;
        const msg = String(e.msg ?? '');
        const locRaw = Array.isArray(e.loc) ? (e.loc as (string | number)[]) : undefined;
        const type = typeof e.type === 'string' ? e.type : undefined;
        items.push({ type, msg, loc: locRaw, input: e.input });
        const locLabel = formatValidationLoc(locRaw);
        i += 1;
        lines.push(locLabel ? `${i}. ${locLabel}: ${msg}` : `${i}. ${msg}`);
      } else if (typeof entry === 'string') {
        i += 1;
        lines.push(`${i}. ${entry}`);
      }
    }
    return {
      message: lines.length ? lines.join('\n') : 'Ошибка валидации',
      items: items.length ? items : undefined,
    };
  }
  if (typeof detail === 'object' && detail !== null && !Array.isArray(detail)) {
    const o = detail as Record<string, unknown>;
    if (typeof o.message === 'string') {
      return { message: o.message };
    }
    if ('msg' in o) {
      return { message: String(o.msg) };
    }
  }
  return { message: String(detail) };
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly detail: string;
  public readonly detailItems?: ApiValidationIssue[];
  public readonly errorType?: string;
  public readonly errorCode?: string;
  /** Одноразовый токен шага 2FA при type=two_factor_required. */
  public readonly tempToken?: string;
  /** Optional structured `details` из error envelope (без потери raw payload). */
  public readonly details?: unknown;
  public readonly rawBody?: unknown;

  public constructor(
    status: number,
    detail: string,
    detailItems?: ApiValidationIssue[],
    meta?: { errorType?: string; errorCode?: string; tempToken?: string; details?: unknown; rawBody?: unknown }
  ) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.detailItems = detailItems;
    this.errorType = meta?.errorType;
    this.errorCode = meta?.errorCode;
    this.tempToken = meta?.tempToken;
    this.details = meta?.details;
    this.rawBody = meta?.rawBody;
  }
}

function mergeHeaders(init?: ApiRequestInit): Headers {
  const h = new Headers(init?.headers ?? undefined);
  if (!h.has('Accept')) h.set('Accept', 'application/json');
  const did = getDeviceId();
  if (did && !h.has('X-Device-Id')) h.set('X-Device-Id', did);
  if (!init?.skipImpersonation && !h.has('X-Impersonate-Organization-Id')) {
    const imp = shouldSendSuperadminOrgHeader() ? getSuperadminOrgId() : null;
    if (imp != null) h.set('X-Impersonate-Organization-Id', String(imp));
  }
  const t = init?.accessToken;
  if (t) {
    h.set('Authorization', `Bearer ${t}`);
  }
  return h;
}

function appendQuery(path: string, query?: QueryRecord): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    params.set(k, String(v));
  }
  const qs = params.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

function throwApiError(status: number, payload: unknown): never {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const rawPayload = payload as { detail: unknown; type?: unknown; code?: unknown; temp_token?: unknown; details?: unknown };
    const raw = rawPayload.detail;
    const { message, items } = formatApiErrorDetail(raw);
    throw new ApiError(status, message, items, {
      errorType: typeof rawPayload.type === 'string' ? rawPayload.type : undefined,
      errorCode: typeof rawPayload.code === 'string' ? rawPayload.code : undefined,
      tempToken: typeof rawPayload.temp_token === 'string' ? rawPayload.temp_token : undefined,
      details: rawPayload.details,
      rawBody: payload,
    });
  }
  throw new ApiError(status, typeof payload === 'string' ? payload : `HTTP ${status}`);
}

function requestInitForFetch(init?: ApiRequestInit): RequestInit {
  if (!init) return {};
  const { accessToken, skipEmbedTokenRefresh, skipImpersonation, ...rest } = init;
  void accessToken;
  void skipEmbedTokenRefresh;
  void skipImpersonation;
  return rest;
}

async function fetchWithEmbed401Retry(
  init: ApiRequestInit | undefined,
  doFetch: (accessToken: string | null | undefined) => Promise<Response>
): Promise<Response> {
  const token = init?.accessToken ?? undefined;
  const response = await doFetch(token);
  if (response.status !== 401 || !token || init?.skipEmbedTokenRefresh) {
    return response;
  }
  const { tryRefreshEmbedAccessToken } = await import('../widget-lib/embed-auth-bridge');
  const newTok = await tryRefreshEmbedAccessToken();
  if (!newTok || newTok === token) {
    return response;
  }
  return doFetch(newTok);
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  const ct = response.headers.get('content-type') ?? '';
  const raw = await response.text();
  if (!ct.includes('application/json')) {
    if (!response.ok) {
      throw new ApiError(response.status, raw || `HTTP ${response.status}`);
    }
    return raw as T;
  }
  const payload = raw.trim() ? JSON.parse(raw) : null;
  if (!response.ok) {
    if (
      response.status === 401 &&
      payload &&
      typeof payload === 'object' &&
      (payload as { type?: unknown }).type === 'context_revoked'
    ) {
      const session = getGlobalEmbedSession();
      const target = session?.embedMode === 'trainer' ? '/embed/trainer/sign-in' : '/embed/sign-in';
      if (session?.orgSlug) clearEmbedSession(session.orgSlug);
      clearGlobalEmbedSession();
      if (typeof window !== 'undefined') {
        window.location.assign(`${target}?role_changed=1`);
      }
    }
    throwApiError(response.status, payload ?? { detail: null });
  }
  return payload as T;
}

export async function apiGet<T>(path: string, query?: QueryRecord, init?: ApiRequestInit): Promise<T> {
  const base = getApiBase();
  const url = `${base}${appendQuery(path.startsWith('/') ? path : `/${path}`, query)}`;
  const response = await fetchWithEmbed401Retry(init, (accessToken) =>
    fetch(url, {
      ...requestInitForFetch(init),
      method: 'GET',
      headers: mergeHeaders({ ...init, accessToken: accessToken ?? undefined }),
    })
  );
  return parseJsonResponse<T>(response);
}

export async function apiPost<T>(path: string, body: unknown, init?: ApiRequestInit): Promise<T> {
  const base = getApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const bodyStr = JSON.stringify(body);
  const response = await fetchWithEmbed401Retry(init, (accessToken) => {
    const headers = mergeHeaders({ ...init, accessToken: accessToken ?? undefined });
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(url, {
      ...requestInitForFetch(init),
      method: 'POST',
      headers,
      body: bodyStr
    });
  });
  return parseJsonResponse<T>(response);
}

export async function apiPatch<T>(path: string, body: unknown, init?: ApiRequestInit): Promise<T> {
  const base = getApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const bodyStr = JSON.stringify(body);
  const response = await fetchWithEmbed401Retry(init, (accessToken) => {
    const headers = mergeHeaders({ ...init, accessToken: accessToken ?? undefined });
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    return fetch(url, {
      ...requestInitForFetch(init),
      method: 'PATCH',
      headers,
      body: bodyStr
    });
  });
  return parseJsonResponse<T>(response);
}

export async function apiDelete<T>(path: string, init?: ApiRequestInit): Promise<T> {
  const base = getApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  const response = await fetchWithEmbed401Retry(init, (accessToken) =>
    fetch(url, {
      ...requestInitForFetch(init),
      method: 'DELETE',
      headers: mergeHeaders({ ...init, accessToken: accessToken ?? undefined }),
    })
  );
  return parseJsonResponse<T>(response);
}
